import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '~/ui/card'
import { Badge } from '~/ui/badge'
import {
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  PlayCircle,
  Calendar,
  TrendingUp,
} from 'lucide-react'

interface VoiceMessage {
  id: string
  ttsStatus: string
  enabled: boolean
  readyForPlayback: boolean
  hasActiveSchedules: boolean
  campaigns?: Array<{
    enabled: boolean
    nextOccurrence?: string
  }>
}

interface VoiceMessagingSummaryProps {
  voiceMessages: VoiceMessage[]
  zoneName?: string
}

export const VoiceMessagingSummary: React.FC<VoiceMessagingSummaryProps> = ({
  voiceMessages,
  zoneName,
}) => {
  const stats = React.useMemo(() => {
    const total = voiceMessages.length
    const enabled = voiceMessages.filter((vm) => vm.enabled).length
    const readyForPlayback = voiceMessages.filter((vm) => vm.readyForPlayback).length
    const withSchedules = voiceMessages.filter((vm) => vm.hasActiveSchedules).length

    const ttsStats = voiceMessages.reduce(
      (acc, vm) => {
        acc[vm.ttsStatus] = (acc[vm.ttsStatus] || 0) + 1
        return acc
      },
      {} as Record<string, number>
    )

    const totalSchedules = voiceMessages.reduce(
      (sum, vm) => sum + (vm.campaigns?.length ?? 0),
      0
    )

    const activeSchedules = voiceMessages.reduce(
      (sum, vm) => sum + (vm.campaigns?.filter((c) => c.enabled)?.length ?? 0),
      0
    )

    const nextOccurrences = voiceMessages
      .flatMap((vm) => vm.campaigns ?? [])
      .filter((c) => c.enabled && c.nextOccurrence)
      .map((c) => new Date(c.nextOccurrence!))
      .sort((a, b) => a.getTime() - b.getTime())

    return {
      total,
      enabled,
      readyForPlayback,
      withSchedules,
      ttsStats,
      totalSchedules,
      activeSchedules,
      nextOccurrence: nextOccurrences[0],
    }
  }, [voiceMessages])

  const getTtsStatusBadge = (status: string, count: number) => {
    const variants = {
      completed: { variant: 'default' as const, color: 'text-green-600', icon: CheckCircle },
      pending: { variant: 'secondary' as const, color: 'text-yellow-600', icon: Clock },
      processing: { variant: 'outline' as const, color: 'text-blue-600', icon: Clock },
      failed: { variant: 'destructive' as const, color: 'text-red-600', icon: XCircle },
    }

    const config = variants[status as keyof typeof variants] || variants.pending
    const Icon = config.icon

    return (
      <div key={status} className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${config.color}`} />
        <span className="text-sm capitalize">{status}</span>
        <Badge variant={config.variant} className="text-xs">
          {count}
        </Badge>
      </div>
    )
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Overview Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4" />
            {zoneName ? `${zoneName} Overview` : 'Messages Overview'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">Total</span>
              <Badge variant="outline">{stats.total}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">Enabled</span>
              <Badge variant="default">{stats.enabled}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">Ready</span>
              <Badge variant="default" className="bg-green-600">
                {stats.readyForPlayback}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TTS Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <PlayCircle className="h-4 w-4" />
            TTS Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(stats.ttsStats).map(([status, count]) =>
              getTtsStatusBadge(status, count)
            )}
            {Object.keys(stats.ttsStats).length === 0 && (
              <div className="text-muted-foreground text-sm">No messages yet</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Scheduling Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4" />
            Scheduling
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">With schedules</span>
              <Badge variant="outline">{stats.withSchedules}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">Total schedules</span>
              <Badge variant="outline">{stats.totalSchedules}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">Active schedules</span>
              <Badge variant="default" className="bg-blue-600">
                {stats.activeSchedules}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Occurrence Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4" />
            Next Playback
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.nextOccurrence ? (
            <div className="space-y-2">
              <div className="text-sm font-medium text-green-600">
                {stats.nextOccurrence.toLocaleDateString()}
              </div>
              <div className="text-muted-foreground text-sm">
                at{' '}
                {stats.nextOccurrence.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
              <div className="text-muted-foreground text-xs">
                {Math.round((stats.nextOccurrence.getTime() - Date.now()) / (1000 * 60))} minutes
                from now
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">No upcoming playbacks</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
